import { Link } from "react-router-dom";
import { Star, MapPin, Clock, Users } from "lucide-react";
import { formatINR } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import WishlistButton from "@/components/WishlistButton";
import { DealFlashBadge, DealCardStrip } from "@/components/DealBadge";

export default function ProductCard({ product }) {
  const img = product.featured_image || product.images?.[0] || "https://images.unsplash.com/photo-1551632811-561732d1e306?w=800";
  return (
    <div className="group bg-white border border-border rounded-2xl overflow-hidden card-hover relative" data-testid={`product-card-${product.slug}`}>
      <DealFlashBadge product={product} />
      <WishlistButton productId={product.id} className="absolute top-3 right-3 z-10" size="sm" />
      <Link to={`/${product.slug}`} className="block">
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={img}
            alt={product.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          {product.is_featured && !product.deal_active && (
            <Badge className="absolute top-3 left-3 bg-saffron text-white border-0 hover:bg-saffron">Featured</Badge>
          )}
          <div className="absolute bottom-3 right-3 bg-white/95 rounded-full px-2 py-1 flex items-center gap-1 text-xs font-medium">
            <Star className="w-3 h-3 fill-saffron text-saffron" />
            {product.rating?.toFixed(1)}
            <span className="text-muted-foreground">({product.review_count})</span>
          </div>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
            {product.destination && (
              <>
                <MapPin className="w-3 h-3" />
                <span>{product.destination}</span>
              </>
            )}
          </div>
          <h3 className="font-display text-lg font-semibold text-foreground mb-1 line-clamp-1 group-hover:text-teal transition-colors">
            {product.title}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{product.short_desc}</p>
          <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
            {product.duration && (
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{product.duration}</span>
            )}
            {product.sub_category && (
              <span className="flex items-center gap-1"><Users className="w-3 h-3" />{product.sub_category}</span>
            )}
          </div>
          <DealCardStrip product={product} />
          <div className="flex items-end justify-between pt-3 border-t border-border">
            <div>
              {product.adult_price > 0 ? (
                <>
                  <div className="text-xs text-muted-foreground">From</div>
                  <div className="text-xl font-bold text-teal">{formatINR(product.adult_price)}</div>
                  <div className="text-[10px] text-muted-foreground">{product.booking_type === "per_vehicle" ? "per vehicle" : product.booking_type === "per_room" ? "per night" : "per person"}</div>
                </>
              ) : (
                <>
                  <div className="text-xs text-muted-foreground">Price</div>
                  <div className="text-sm font-semibold text-blue-600">On Request</div>
                </>
              )}
            </div>
            <div className="bg-saffron text-white text-sm font-medium px-4 py-2 rounded-lg group-hover:bg-saffron-dark transition-colors">
              {product.adult_price > 0 ? "View" : "Get Quote"}
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}
